<template>
  <request-header-login-wrapper>
    <input-group :title="`${title}のお願い`" v-if="!sendComplete">
      <applicant-message-view
        v-if="requestHeaderId != null && messageType != null && messageId != null"
        :requestHeaderId="requestHeaderId"
        :messageType="messageType"
        :messageId="messageId"
      />
      <el-form :model="scheduleAnswer" :rules="rules" ref="formRef" class="mt-10">
        <el-row class="py">
          <el-col :span="5" class="align-middle pb">
            <input-label :required="true">日程</input-label>
          </el-col>
          <el-col>
            <schedule-answer-radio-group
              v-if="messageId != null"
              :messageId="messageId"
              @update:schedule_inquiry_id="scheduleInquiryId = $event"
              @update:answer="updateAnswer($event)"
              :disabled="!canAnswer"
            />
          </el-col>
        </el-row>
        <el-row>
          <el-col :span="5" class="align-middle pb">
            <input-label :required="false">メッセージ</input-label>
          </el-col>
          <el-col :span="24">
            <el-form-item prop="body" class="send-body">
              <jr-textarea :rows="10" v-model="scheduleAnswer.body" :isEdit="true" :disabled="!canAnswer" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <el-row class="mt-3">
        <el-col :span="3" class="vertical-align">
          <div class="actions">
            <!-- 送信者へ連絡ボタン -->
            <div>
              <push-sender-question
                type="applicant"
                :requestHeaderId="requestHeaderId"
                :messageType="messageType"
                :messageId="messageId"
              />
            </div>
          </div>
        </el-col>
        <el-col :span="18" class="align-center">
          <div class="actions">
            <div>
              <!-- 送信ボタン -->
              <applicant-schedule-answer-send
                v-if="scheduleInquiryId != null"
                :scheduleInquiryId="scheduleInquiryId"
                :scheduleAnswer="scheduleAnswer"
                :formRef="formRef"
                :disabled="!canAnswer"
                @update:isSent="sendComplete = $event"
              />
            </div>
          </div>
        </el-col>
        <el-col :span="3" class="vertical-align justify-end">
          <div class="actions align-left">
            <!-- 回答履歴はこちらボタン -->
            <push-applicant-history :requestHeaderId="requestHeaderId" />
          </div>
        </el-col>
      </el-row>
    </input-group>
    <input-group title="送信完了" v-else>
      <p>メッセージを送信しました。</p>
      <el-row class="mt-3">
        <el-col :span="12">
          <jr-button
            type="info"
            class="return-btn"
            @click="
              () => {
                sendComplete = false
              }
            "
            >戻る</jr-button
          >
        </el-col>
      </el-row>
    </input-group>
  </request-header-login-wrapper>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElForm } from 'element-plus'
import { AxiosResponse } from 'axios'

import InputGroup from '@/components/layout/InputGroup.vue'
import RequestHeaderLoginWrapper from '@/components/auth-wrappers/RequestHeaderLoginWrapper.vue'
import ApplicantMessageView from '@/components/layout/ApplicantMessageView.vue'
import PushSenderQuestion from '@/components/actions/PushSenderQuestion.vue'
import PushApplicantHistory from '@/components/actions/PushApplicantHistory.vue'
import JrButton from '@/components/jr-parts/JrButton.vue'
import ScheduleAnswerRadioGroup from '@/components/inputs/ScheduleAnswerRadioGroup.vue'
import JrTextarea from '@/components/jr-parts/JrTextarea.vue'
import InputLabel from '@/components/inputs/InputLabel.vue'
import ApplicantScheduleAnswerSend from '@/components/actions/ApplicantScheduleAnswerSend.vue'

import MessageTypeLogic from '@/services/messageTypeLogic'
import RequestHeaderLogic from '@/services/requestHeaderLogic'
import Logger from '@/services/logger'
import { RequestHeaderDetail, ScheduleAnswerApplicantSend, ScheduleAnswerDatePost } from '@/api'
import { scheduleAnswerRule } from '@/constants/rules'
import { ReadRequest as RequestHeaderReadRequest } from '@/requests/requestHeader'
import { MessageTypes } from '@/constants/messageTypes'

/**
 * 日程調整回答
 */
export default defineComponent({
  name: 'ApplicantScheduleMessage',
  components: {
    InputGroup,
    RequestHeaderLoginWrapper,
    ApplicantMessageView,
    PushSenderQuestion,
    PushApplicantHistory,
    JrButton,
    JrTextarea,
    InputLabel,
    ScheduleAnswerRadioGroup,
    ApplicantScheduleAnswerSend,
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const formRef = ref<InstanceType<typeof ElForm>>()
    // 申請情報
    const requestHeaderId = computed(() => {
      const id = Number(route.params.request_header_id)
      return Number.isNaN(id) ? null : id
    })
    // メッセージ種別
    const messageType = computed(() => {
      const type = Number(route.params.message_type)
      return Number.isNaN(type) ? null : type
    })
    // メッセージID
    const messageId = computed(() => {
      const id = Number(route.params.message_id)
      return Number.isNaN(id) ? null : id
    })
    // タイトル
    const title = ref<string>('')
    const scheduleInquiryId = ref<number | null>(null)
    // 送信メッセージ
    const scheduleAnswer = reactive<ScheduleAnswerApplicantSend>({
      body: '',
      schedule_answer_date: [],
    })
    // 送信完了
    const sendComplete = ref<boolean>(false)
    const canAnswer = ref<boolean>(false)

    /**
     * 日付回答の更新
     */
    const updateAnswer = (answers: ScheduleAnswerDatePost[]): void => {
      scheduleAnswer.schedule_answer_date.splice(0)
      scheduleAnswer.schedule_answer_date.push(...answers)
    }

    watch(
      () => [requestHeaderId, messageType, messageId],
      async () => {
        if (route.name !== 'ApplicantScheduleMessage') {
          // 遷移中は何もしない
          return
        }
        // 初期化
        canAnswer.value = false
        title.value = ''
        sendComplete.value = false

        // パラメータチェック
        if (requestHeaderId.value == null || messageType.value == null || messageId.value == null) {
          Logger.info(
            `URLパラメータが不正です 申請基本ID: ${route.params.request_header_id} 種別: ${route.params.message_type} メッセージID: ${route.params.message_id}`
          )
          router.push({ name: 'NotFound' })
          return
        }
        const logic = new MessageTypeLogic(messageType.value, requestHeaderId.value)
        // 開ける種別かどうか確認する
        if (!logic.isScheduleType()) {
          Logger.info(`メッセージ種別が不正です。種別: ${messageType.value}`)
          router.push({ name: 'NotFound' })
          return
        } else if (!logic.isApplicantReceiveType()) {
          // 協議者が受信できるメッセージか確認
          Logger.info(`協議者が受信できないメッセージ種別です。種別: ${messageType.value}`)
          router.push({ name: 'NotFound' })
          return
        }

        // 回答可能かをチェック
        const request = new RequestHeaderReadRequest(requestHeaderId.value)
        if (!(await request.exec())) {
          return
        }
        const { data: requestHeaderData } = request.getResponse() as AxiosResponse<RequestHeaderDetail>
        const requestHeaderLogic = new RequestHeaderLogic(requestHeaderData)
        if (messageType.value === MessageTypes.P_PD_ADJUST_DATE) {
          canAnswer.value = requestHeaderLogic.canAnswerProximityPriorDiscussionSchedule()
        } else if (messageType.value === MessageTypes.P_WS_ADJUST_DATE) {
          canAnswer.value = requestHeaderLogic.canAnswerProximitySchedule()
        } else if (messageType.value === MessageTypes.B_WS_ADJUST_DATE) {
          canAnswer.value = requestHeaderLogic.canAnswerBoundarySchedule()
        }
        Logger.debug(`回答可能: ${canAnswer.value}`)

        // タイトル設定
        title.value = logic.getTypeName()
      },
      { immediate: true, deep: true }
    )

    return {
      formRef,
      title,
      requestHeaderId,
      messageType,
      messageId,
      scheduleAnswer,
      sendComplete,
      scheduleInquiryId,
      updateAnswer,
      rules: scheduleAnswerRule,
      canAnswer,
    }
  },
})
</script>

<style lang="scss" scoped>
.input-group {
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
}

.actions {
  flex-wrap: wrap;
  margin-left: -5px;
  margin-right: -5px;
  div {
    display: inline-block;
    .el-button {
      margin-left: 5px;
      margin-right: 5px;
    }
  }
}

.vertical-align ::v-deep(.el-button) {
  padding-bottom: 0;
}

.vertical-align {
  display: flex;
  align-items: flex-end;
}
</style>
